import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, CircleAlert, Hash, LockKeyhole, RefreshCcw, ShieldCheck, X } from "lucide-react";
import ModalScrim from "../components/ModalScrim";
import DataRowTable from "../components/DataRowTable";
import RowActionBar from "../components/RowActionBar";
import { Badge, ConfidenceBreakdown, recTone } from "../lib/ui";
import { getAuthorities } from "../lib/api";
import RoleHomeShell from "../components/RoleHomeShell";
import StorySpine from "../components/StorySpine";
import { confidenceBreakdown, useAppGo, withApp } from "../lib/story";
import { canSignGates, navItemAllowed } from "../lib/master";
import {
  DECISION_DRILL,
  DECISION_FLOWS,
  DECISION_FOCUS_DETAIL,
  DECISION_FOCUS_TITLE,
} from "../lib/decisionFlows";
import { linkedApplicationRows } from "../lib/dependencyApps";
import SubFlowNav, { FocusPanel, MetricStrip, SubFlowPager } from "../components/SubFlow";
import { useSubFlow, subflowHref } from "../lib/subflow";
import OptionsComparison from "../components/OptionsComparison";
import ConformancePanel from "../components/ConformancePanel";

const SEVEN_R = ["Rehost", "Replatform", "Refactor / Rearchitect", "Repurchase", "Retain", "Retire", "Relocate"];

export default function Decision({ app, portfolio, onAuthorize, onReassess, onSaveRecommendation, user }) {
  const go = useAppGo(app?.id);
  const { focus } = useSubFlow("overview");
  const [modal, setModal] = useState(false);
  const [reassessModal, setReassessModal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openAlt, setOpenAlt] = useState(null);

  if (!app) return null;

  const intel = app.decision_intelligence || {};
  const why = intel.why || app.why || {};
  const contract = intel.score_contract || {};
  const rec = (intel.alternatives || app.alternatives || []).find((a) => a.recommended) || {};
  const active = (app.mdrs || []).filter((m) => !m.superseded_by).slice(-1)[0];
  const authorized = active?.state === "AUTHORIZED";
  const lenses7c = intel.lenses?.["7c"] || app.assessments?.["7c"] || [];
  const lenses7s = intel.lenses?.["7s"] || app.assessments?.["7s"] || [];
  const deps = intel.dependencies || {};
  const explain = intel.explainability || {};
  const alternatives = intel.alternatives || app.alternatives || [];
  const conf = confidenceBreakdown(app);
  const whyBlocked = !app.sponsor || !why.driver || !why.problem || !(why.outcomes || []).length;
  const evidenceBlocked = app.input_foundation?.readiness && !app.input_foundation.readiness.eligible_for_authorization;
  const mayAssess = navItemAllowed("/assessment", user?.guide);
  const isOverview = focus === "overview";

  return (
    <div className="inner-page subflow-page" data-testid="decision-intelligence">
      <RoleHomeShell guide={user?.guide || {}} portfolio={portfolio} app={app} appId={app?.id} count={portfolio?.applications?.length} />

      {!isOverview && (
        <Link to={withApp("/decision", app.id)} className="analytics-back" data-testid="decision-back">
          <ChevronLeft size={16} /> Back to 7R overview
        </Link>
      )}

      <FocusPanel
        kicker={`DECIDE · ${app.id}`}
        title={
          isOverview
            ? (app.recommendation || "7R decision pending")
            : (DECISION_FOCUS_TITLE[focus] || "7R decision")
        }
        detail={
          authorized && focus === "authorize"
            ? "MDR sealed — immutable."
            : (DECISION_FOCUS_DETAIL[focus] || "One sub-topic at a time. AI cannot authorize.")
        }
        badge={<Badge tone={authorized ? "green" : "amber"}>{authorized ? "MDR AUTHORIZED" : "REVIEW READY"}</Badge>}
        testId="decision-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={DECISION_FLOWS} focus={focus} appId={app.id} testId="decision-subflow" />
          <StorySpine current="decide" compact />
        </>
      )}

      {isOverview && (
        <>
          {whyBlocked && (
            <div className="decision-input-banner blocked" data-testid="why-first-block">
              <div>
                <p className="kicker">WHY-FIRST · PROCEED BLOCKED</p>
                <strong>Sponsor, driver, problem and outcomes are required before MDR authorization.</strong>
              </div>
              {mayAssess && (
                <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=why")}>Open WHY</button>
              )}
            </div>
          )}

          <MetricStrip
            items={[
              { label: "Recommended", value: app.recommendation || "—", testId: "decision-kpi-rec" },
              { label: "Suitability", value: rec.suitability != null ? `${rec.suitability}%` : "—", testId: "decision-kpi-suit" },
              { label: "Eligibility", value: rec.eligibility || "—", testId: "decision-kpi-elig" },
              { label: "MDR", value: authorized ? "AUTHORIZED" : "PENDING", testId: "decision-kpi-mdr" },
            ]}
          />

          <p className="muted small-note">{why.driver || "WHY not recorded"} · {app.evidence?.coverage || 0}% evidence coverage</p>

          <OptionsComparison comparison={intel.options_comparison} />

          <ConformancePanel app={app} />

          <section className="feature-panel analytics-panel" data-testid="decision-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {DECISION_DRILL.map(({ id, label }) => (
                <Link
                  key={id}
                  to={subflowHref("/decision", app.id, id)}
                  className="lifecycle-phase-link"
                  data-testid={`decision-drill-${id}`}
                >
                  <strong>{label}</strong>
                  <span>{DECISION_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>

          {!authorized && canSignGates(user?.guide || user?.role) && (
            <div className="focus-actions">
              <button
                type="button"
                className="primary-button"
                data-testid="decision-overview-authorize"
                onClick={() => go("/decision?focus=authorize")}
                disabled={whyBlocked || evidenceBlocked}
              >
                Seal MDR <ArrowUpRight size={15} />
              </button>
            </div>
          )}
        </>
      )}

      {focus === "why" && (
        <>
          {whyBlocked && (
            <div className="decision-input-banner blocked" data-testid="why-first-block">
              <div>
                <p className="kicker">WHY-FIRST · PROCEED BLOCKED</p>
                <strong>Sponsor, driver, problem and outcomes are required before MDR authorization.</strong>
              </div>
              {mayAssess && <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=why")}>Open WHY</button>}
            </div>
          )}
          {(app.rationalization || app.brd?.rationalization) && (
            <div className="decision-input-banner ready" data-testid="rationalization-banner">
              <div>
                <p className="kicker">RATIONALIZATION · BEFORE 7R</p>
                <strong>{(app.rationalization || app.brd.rationalization).outcome}</strong>
                <p className="muted small-note">{(app.rationalization || app.brd.rationalization).reason}</p>
              </div>
            </div>
          )}
          {(app.target || app.brd?.cloud) && (
            <div className={`decision-input-banner ${(app.target || app.brd.cloud).destination_neutral ? "ready" : ""}`} data-testid="cloud-not-chosen">
              <div>
                <p className="kicker">CLOUD / TARGET · DESTINATION-NEUTRAL</p>
                <strong>{(app.target || app.brd.cloud).cloud || "Not Chosen"}</strong>
                <p className="muted small-note">{(app.target || app.brd.cloud).reason || "Cloud Not Chosen is a valid POC outcome."}</p>
              </div>
            </div>
          )}
          <div className="why-banner" data-testid="decision-why">
            <div>
              <span className="recommendation-label">WHY · WHAT MUST THIS DECISION SOLVE</span>
              <h2>{why.driver}</h2>
              <p>{why.problem}</p>
              {why.risk_of_nothing && <p className="muted small-note"><strong>Risk of doing nothing:</strong> {why.risk_of_nothing}</p>}
            </div>
            <div className="outcome-list">
              {(why.outcomes || []).map((o) => <span key={o}><Check size={14} /> {o}</span>)}
            </div>
          </div>
        </>
      )}

      {focus === "scores" && (
        <>
          <div className="score-contract" data-testid="score-contract">
            {[
              ["Suitability", rec.suitability != null ? `${rec.suitability}%` : "—", contract.suitability],
              ["Eligibility", rec.eligibility || "—", contract.eligibility],
              ["Confidence", "components below", contract.confidence],
              ["Authorization", authorized ? "AUTHORIZED" : "PENDING HUMAN", contract.authorization],
            ].map(([label, value, meaning]) => (
              <div key={label}>
                <small>{label.toUpperCase()}</small>
                <strong>{value}</strong>
                <p>{meaning}</p>
              </div>
            ))}
          </div>
          <ConfidenceBreakdown breakdown={conf} />
          {app.input_foundation?.readiness && (
            <div className={`decision-input-banner ${app.input_foundation.readiness.eligible_for_authorization ? "ready" : "blocked"}`} data-testid="decision-input-banner">
              <div>
                <p className="kicker">EVIDENCE QUALITY · FEEDS CONFIDENCE</p>
                <strong>{explain.confidence_drivers || `Coverage ${app.input_foundation.readiness.coverage}%`}</strong>
                <p className="muted small-note">{app.input_foundation.readiness.note}</p>
              </div>
              {mayAssess && <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=readiness")}>Open evidence quality</button>}
            </div>
          )}
        </>
      )}

      {focus === "lenses" && (
        <section className="feature-panel" data-testid="decision-7c7s">
          <div className="panel-header">
            <div>
              <p className="kicker">7C / 7S · LENSES FED BY EVIDENCE</p>
              <h2>Assessment that ranks the 7R set</h2>
            </div>
            <Badge tone="cyan">Not a recommendation</Badge>
          </div>
          <div className="assessments-grid">
            <div className="assessments-col">
              <h4>7C</h4>
              {lenses7c.map((a) => (
                <div className="assessment-row" key={a.lens}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                  <small>{a.note}</small>
                </div>
              ))}
            </div>
            <div className="assessments-col">
              <h4>7S</h4>
              {lenses7s.map((a) => (
                <div className="assessment-row" key={a.lens}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar cyan" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                  <small>{a.note}</small>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {focus === "dependencies" && (
        <section className="feature-panel" data-testid="decision-dependencies">
          <div className="panel-header">
            <div>
              <p className="kicker">DEPENDENCIES · COUPLING THAT CHANGES 7R</p>
              <h2>{deps.total || app.dependencies?.nodes?.length || 0} mapped systems</h2>
            </div>
            <Badge tone="amber">{deps.unknown_owners || 0} unknown owners</Badge>
          </div>
          <p className="muted small-note">{deps.effect}</p>
          {linkedApplicationRows(app, portfolio).length > 0 && (
            <div className="persona-dep-app-list compact" data-testid="decision-dependent-apps">
              <p className="kicker">COUPLED APPLICATIONS</p>
              <ul>
                {linkedApplicationRows(app, portfolio).map((row) => (
                  <li key={row.id} data-testid={`decision-dependent-app-${row.id.toLowerCase()}`}>
                    <div>
                      <strong>{row.id}</strong>
                      <span>{row.name}</span>
                    </div>
                    <Link className="text-button" to={withApp("/overview", row.id)}>Open</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="dep-intel-list">
            {(deps.nodes || []).map((n) => (
              <div key={n.id} className={`dep-intel-row kind-${n.kind}`} data-testid={`decision-dep-${(n.id || "").toLowerCase()}`}>
                <strong>{n.label}</strong>
                <Badge tone={n.kind === "critical" ? "rose" : n.kind === "conflict" ? "amber" : n.owner === "unknown" ? "amber" : "slate"}>{n.kind}</Badge>
                <span>{n.decision}</span>
                {n.blocks?.length > 0 && <small>Blocks {n.blocks.join(", ")}</small>}
              </div>
            ))}
            {!(deps.nodes || []).length && <p className="muted small-note">No dependency graph seeded for this application.</p>}
          </div>
        </section>
      )}

      {focus === "alternatives" && (
        <>
          <section className="feature-panel large-decision">
            <div className="recommendation-banner">
              <div>
                <span className="recommendation-label">RECOMMENDED 7R STRATEGY</span>
                <h1>{app.recommendation}</h1>
                <p>{app.recommendation_reason}</p>
              </div>
              <div className="big-score">
                <strong>{conf.components.find((c) => c.id === "coverage")?.value || `${app.evidence?.coverage || 0}%`}</strong>
                <span>evidence coverage</span>
              </div>
            </div>
          </section>
          {(intel.options_comparison || {}).options?.length > 0 && (
            <OptionsComparison comparison={intel.options_comparison} testId="options-comparison-drill" />
          )}
          <section className="feature-panel">
            <DataRowTable
              testId="alternatives-table"
              columns={[
                {
                  key: "strategy",
                  label: "Strategy",
                  render: (a) => <><b>{a.strategy}</b>{a.recommended && <em> · Recommended</em>}</>,
                },
                {
                  key: "suitability",
                  label: "Suitability",
                  render: (a) => <><Bar value={a.suitability} tone={recTone(a.strategy)} /> {a.suitability}%</>,
                },
                {
                  key: "eligibility",
                  label: "Eligibility",
                  render: (a) => (
                    <Badge tone={a.eligibility === "Eligible" || a.eligibility === "Recommended" ? "green" : a.eligibility === "Conditional" ? "amber" : "rose"}>
                      {a.eligibility}
                    </Badge>
                  ),
                },
                { key: "confidence", label: "Confidence", render: (a) => `${a.confidence}%` },
                { key: "cost", label: "Cost" },
                { key: "risk", label: "Risk" },
                { key: "note", label: "Notes", render: (a) => a.note || "—" },
              ]}
              rows={alternatives}
              rowKey="strategy"
              pageSize={10}
              hint="View opens explainable ranking detail. Recommended strategy is highlighted in the workspace."
              renderActions={(row, stableId) => (
                <RowActionBar
                  testId={`alt-actions-${stableId}`}
                  rowId={stableId}
                  view
                  review={!!row.blockers?.length}
                  onView={() => setOpenAlt(row.strategy)}
                  onReview={() => setOpenAlt(row.strategy)}
                />
              )}
            />
            {openAlt && <AltExplainPanel alternatives={alternatives} strategy={openAlt} onClose={() => setOpenAlt(null)} />}
          </section>
        </>
      )}

      {focus === "explain" && (
        <section className="feature-panel">
          <div className="panel-header">
            <div>
              <p className="kicker">EXPLAINABLE DECISION</p>
              <h2>Evidence behind the ranking</h2>
            </div>
          </div>
          <div className="evidence-columns">
            <div>
              <h3>Supporting</h3>
              {(explain.supporting || []).slice(0, 8).map((f) => (
                <div className="evidence-line" key={f.id}><Check size={15} />{f.label}: {f.value}</div>
              ))}
            </div>
            <div>
              <h3>Constraints</h3>
              {(explain.constraints || []).slice(0, 8).map((f) => (
                <div className="evidence-line warning" key={f.id}><CircleAlert size={15} />{f.label}: {f.value}</div>
              ))}
            </div>
          </div>
        </section>
      )}

      {focus === "authorize" && (
        <>
          {!authorized && !(app.recommendation || "").trim() && canWriteWhy(user?.guide) && onSaveRecommendation && (
            <RecordSevenR app={app} onSaveRecommendation={onSaveRecommendation} />
          )}
          {(whyBlocked || evidenceBlocked) && !authorized && (
            <div className="decision-input-banner blocked" data-testid="authorize-blockers" style={{ marginTop: 12 }}>
              <div>
                <p className="kicker">SEAL BLOCKED</p>
                <strong>
                  {whyBlocked
                    ? "Sponsor, driver, problem and outcomes are required before MDR authorization."
                    : "Evidence readiness is not eligible for authorization. Resolve HIGH UNKNOWN / CONFLICTING facts first."}
                </strong>
              </div>
              {mayAssess && (
                <button type="button" className="secondary-button" onClick={() => go(whyBlocked ? "/assessment?focus=why" : "/evidence")}>
                  {whyBlocked ? "Open WHY" : "Open Evidence"}
                </button>
              )}
            </div>
          )}
          <AuthorizePanel
            app={app}
            active={active}
            authorized={authorized}
            whyBlocked={whyBlocked}
            evidenceBlocked={evidenceBlocked}
            historyOpen={historyOpen}
            setHistoryOpen={setHistoryOpen}
            setModal={setModal}
            setReassessModal={setReassessModal}
            user={user}
          />
        </>
      )}

      {!isOverview && (
        <SubFlowPager items={DECISION_FLOWS} focus={focus} appId={app.id} testId="decision-pager" />
      )}

      {modal && <AuthorizeModal app={app} user={user} onClose={() => setModal(false)} onAuthorize={onAuthorize} />}
      {reassessModal && <ReassessModal app={app} onClose={() => setReassessModal(false)} onReassess={onReassess} />}
    </div>
  );
}

function Bar({ value, tone }) {
  return <span className="alt-bar-wrap"><span className={`alt-bar ${tone}`} style={{ width: `${value}%` }} /></span>;
}

function AltExplainPanel({ alternatives, strategy, onClose }) {
  const a = alternatives.find((x) => x.strategy === strategy) || {};
  const x = a.explain || {};
  return (
    <div className="alt-explain" data-testid="alt-explain-panel">
      <div className="alt-explain-head">
        <div>
          <p className="kicker">CLICK-TO-EXPLAIN · {x.verdict || "review"}</p>
          <h3>Why {a.strategy} scored this way</h3>
        </div>
        <button type="button" className="text-button" onClick={onClose}>Close</button>
      </div>
      <p>{x.because || a.note}</p>
      <div className="alt-explain-grid">
        <div><small>SUITABILITY</small><strong>{a.suitability}%</strong><p>{x.suitability_means}</p></div>
        <div><small>ELIGIBILITY</small><strong>{a.eligibility}</strong><p>{x.eligibility_means}</p></div>
        <div><small>CONFIDENCE</small><strong>evidence quality</strong><p>{x.confidence_means}</p></div>
      </div>
      <div className="alt-explain-evidence-split" data-testid="alt-hypothesis-evidence">
        <div>
          <small>SUPPORTING</small>
          {(x.supporting || []).map((e) => <div key={e.id}><code>{e.id}</code> {e.label}: {e.value} <em>{e.state}</em></div>)}
        </div>
        <div>
          <small>CONTRADICTORY</small>
          {(x.contradictory || []).map((e) => <div key={e.id}><code>{e.id}</code> {e.label}: {e.value} <em>{e.state}</em></div>)}
        </div>
        <div>
          <small>UNKNOWN</small>
          {(x.unknown || []).map((e) => <div key={e.id}><code>{e.id}</code> {e.label}: {e.value} <em>{e.state}</em></div>)}
        </div>
      </div>
    </div>
  );
}

function AuthorizePanel({ app, active, authorized, whyBlocked, evidenceBlocked, historyOpen, setHistoryOpen, setModal, setReassessModal, user }) {
  const sealed = app?.human_governance?.audit?.sealed_events || [];
  return (
    <div className="decision-layout" style={{ marginTop: 17 }}>
      <aside className="side-panel full-width">
        <p className="kicker">MDR · IMMUTABLE DECISION RECORD</p>
        <h3 data-testid="mdr-version">{active?.version}</h3>
        <div className="mdr-state" data-testid="mdr-state">
          <span className={authorized ? "status-dot" : "status-dot amber-dot"} /> {(active?.state || "").replaceAll("_", " ")}
        </div>
        {authorized && (
          <div className="mdr-details">
            <div><Hash size={13} /><span data-testid="mdr-hash">{active.hash}</span></div>
            <div><ShieldCheck size={13} /><span>{active.authorized_by} · {active.role}</span></div>
            <div><Check size={13} /><span>{new Date(active.authorized_at).toUTCString().slice(5, 22)} UTC</span></div>
            {active.authorized_strategy && (
              <div data-testid="mdr-authorized-strategy">
                <ShieldCheck size={13} />
                <span><b>Authorized strategy:</b> {active.authorized_strategy}</span>
              </div>
            )}
            <div><Check size={13} /><span data-testid="mdr-persisted">Persisted in applications + mdr_audit</span></div>
          </div>
        )}
        {app.wave_eligible && (
          <div className="wave-eligible-badge" data-testid="wave-eligible-badge">
            <ShieldCheck size={13} /> Wave planning eligible
          </div>
        )}
        <p className="muted">Authorization creates an immutable snapshot with a SHA-256 integrity hash and stores it in Mongo.</p>
        {!authorized && canSignGates(user?.guide || user?.role) ? (
          <button data-testid="authorize-mdr-button" className="primary-button full" onClick={() => setModal(true)} disabled={whyBlocked || evidenceBlocked}>
            <ShieldCheck size={16} /> Authorize MDR
          </button>
        ) : !authorized ? (
          <p className="muted small-note" data-testid="authorize-mdr-hidden">Your role cannot authorize an MDR.</p>
        ) : canSignGates(user?.guide || user?.role) ? (
          <button data-testid="reassess-mdr-button" className="secondary-button full" onClick={() => setReassessModal(true)}>
            <RefreshCcw size={15} /> Material change · successor MDR
          </button>
        ) : null}
        <button data-testid="mdr-history-toggle" className="text-button" type="button" onClick={() => setHistoryOpen(!historyOpen)}>
          {historyOpen ? "Hide MDR history" : "See MDR history"} <ChevronRight size={14} />
        </button>
        {historyOpen && (
          <div className="mdr-history" data-testid="mdr-history-list">
            {(app.mdrs || []).slice().reverse().map((m) => (
              <div key={m.version} className={m.superseded_by ? "mdr-item superseded" : "mdr-item"}>
                <strong>{m.version}</strong>
                <span>{m.state.replaceAll("_", " ")}</span>
              </div>
            ))}
            {sealed.length > 0 && (
              <div className="mdr-sealed-events" data-testid="mdr-sealed-events" style={{ marginTop: 12 }}>
                <p className="kicker">DATABASE · mdr_audit</p>
                {sealed.slice(0, 8).map((ev) => (
                  <div key={ev.event_id || `${ev.version}-${ev.created_at}`} className="mdr-item">
                    <strong>{ev.outcome || "event"} · {ev.version || "—"}</strong>
                    <span>{ev.authorized_by || "—"} · {ev.hash || ev.detail || ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="policy-note">
          <LockKeyhole size={14} />
          <span>AI advisory only<br /><b>Human authority required</b></span>
        </div>
      </aside>
    </div>
  );
}

function apiErrorDetail(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item))).join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return err?.message || fallback;
}

function AuthorizeModal({ app, user, onClose, onAuthorize }) {
  const strategyOptions = (app.alternatives || []).filter((a) => a.eligibility !== "Blocked" && a.eligibility !== "Not recommended");
  const fallbackStrategies = strategyOptions.length ? strategyOptions.map((a) => a.strategy) : SEVEN_R;
  const [name, setName] = useState(user?.name || "Alex Morgan");
  const [role, setRole] = useState(user?.role || "Product Owner");
  const [rationale, setRationale] = useState("");
  const [strategy, setStrategy] = useState(app.recommendation || fallbackStrategies[0] || "Rehost");
  const [authorities, setAuthorities] = useState(["Enterprise Architect", "Solution Architect", "Product Owner", "Business Owner", "Modernization Lead", "Security Architect"]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    getAuthorities()
      .then(({ data }) => {
        const roles = Array.isArray(data?.approver_roles)
          ? data.approver_roles
          : Array.isArray(data?.legacy_decision_roles)
            ? data.legacy_decision_roles
            : null;
        if (roles?.length) {
          setAuthorities(roles);
          if (user?.role && roles.includes(user.role)) setRole(user.role);
        }
      })
      .catch(() => {});
  }, [user?.role]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!rationale.trim()) { setErr("Rationale is required — the seal is not stored without it."); return; }
    if (!(app.recommendation || "").trim() && !strategy) {
      setErr("Record a 7R recommendation before sealing the MDR.");
      return;
    }
    setBusy(true);
    try {
      await onAuthorize({
        approver: name,
        role: user?.role || role,
        rationale: rationale.trim(),
        authorized_strategy: strategy,
      });
      onClose();
    } catch (ex) {
      setErr(apiErrorDetail(ex, "Authorization failed — nothing was sealed in the database."));
    } finally { setBusy(false); }
  };
  const isOverride = strategy !== app.recommendation;
  return (
    <ModalScrim testId="authorization-modal">
      <form className="modal wide" onSubmit={submit}>
        <button data-testid="close-authorization-modal" type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">HUMAN AUTHORIZATION · POLICY v3.2</p>
        <h2>Seal MDR for {app.name}</h2>
        <p className="muted small-note">This writes the immutable MDR into Mongo (`applications.mdrs` + `mdr_audit`). Session role is the authority — payload role is recorded for display only.</p>
        <label>Approver<input data-testid="approver-name-input" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Authority role
          <select data-testid="approver-role-input" value={role} onChange={(e) => setRole(e.target.value)} required>
            {(Array.isArray(authorities) ? authorities : []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label>Authorized 7R strategy
          <select data-testid="authorized-strategy-input" value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            {fallbackStrategies.map((item) => {
              const label = typeof item === "string" ? item : item;
              const alt = strategyOptions.find((a) => a.strategy === label);
              return (
                <option key={label} value={label}>{label}{alt?.recommended ? " (recommended)" : ""}</option>
              );
            })}
          </select>
        </label>
        {isOverride && <div className="modal-warning" data-testid="override-warning"><CircleAlert size={15} /> Human override · rationale min 20 chars.</div>}
        <label>Rationale <span className="required-star">*</span>
          <textarea data-testid="approver-rationale-input" rows={3} required minLength={isOverride ? 20 : 1} value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Why this MDR is sealed now…" />
        </label>
        {err && <div className="error-message" data-testid="authorization-error">{err}</div>}
        <button data-testid="confirm-authorization-button" className="primary-button full" type="submit" disabled={busy}>
          {busy ? "Sealing to database…" : "Authorize & create immutable MDR"} <ArrowUpRight size={16} />
        </button>
      </form>
    </ModalScrim>
  );
}

function RecordSevenR({ app, onSaveRecommendation }) {
  const [strategy, setStrategy] = useState(app.recommendation || "Rehost");
  const [reason, setReason] = useState(app.recommendation_reason || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onSaveRecommendation({ recommendation: strategy, recommendation_reason: reason });
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not record 7R recommendation");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="feature-panel" data-testid="record-seven-r" style={{ marginTop: 17 }}>
      <p className="kicker">FROM-ZERO · 7R</p>
      <h2>Record recommendation</h2>
      <p className="muted small-note">Required when the application has no 7R recommendation yet. Does not authorize an MDR.</p>
      <form onSubmit={submit}>
        <label>7R strategy
          <select data-testid="record-7r-strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            {SEVEN_R.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>Why this strategy<textarea data-testid="record-7r-reason" rows={3} required minLength={8} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        {err && <div className="error-message">{err}</div>}
        <button data-testid="record-7r-submit" className="primary-button" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save 7R recommendation"}
        </button>
      </form>
    </section>
  );
}

function ReassessModal({ app, onClose, onReassess }) {
  const [reason, setReason] = useState("New regulatory scan surfaced 4 critical CVEs · security posture materially changed.");
  const [trigger, setTrigger] = useState("SECURITY_CVE_CHANGE");
  const [name, setName] = useState("Alex Morgan");
  const [role, setRole] = useState("Enterprise Architect");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onReassess({ reason, trigger, approver: name, role });
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Reassessment failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalScrim testId="reassess-modal">
      <form className="modal wide" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="kicker">MATERIAL CHANGE · SELECTIVE REASSESSMENT</p>
        <h2>Create successor MDR</h2>
        <label>Trigger
          <select data-testid="reassess-trigger" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
            <option>SECURITY_CVE_CHANGE</option>
            <option>NEW_DEPENDENCY</option>
            <option>TECHNOLOGY_RUNTIME_CHANGE</option>
          </select>
        </label>
        <label>Rationale<textarea data-testid="reassess-reason" rows={3} required value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        <label>Approver<input data-testid="reassess-approver" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        {err && <div className="error-message" data-testid="reassess-error">{err}</div>}
        <button data-testid="confirm-reassess-button" className="primary-button full" type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create successor MDR"} <ArrowUpRight size={16} />
        </button>
      </form>
    </ModalScrim>
  );
}
